import { useCallback, useState } from 'react';
import { api } from '../services/api.ts';

interface UserInfo {
  name: string;
  email: string;
  imageUrl: string;
}

interface BirthdayEvent {
  name: string;
  date: Date;
  reminderDays: number[];
  appEventId: string;
  emoji?: string;
}

const useGoogleCalendar = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  // Check if the user is connected to Google Calendar, but only when explicitly called
  const checkGoogleConnection = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/auth/google/status');
      if (response.data.userInfo) {
        setUserInfo({
          name: response.data.userInfo.name,
          email: response.data.userInfo.email,
          imageUrl: response.data.userInfo.picture
        });
      } else {
        setUserInfo(null);
      }
      return response.data.connected;
    } catch (error) {
      console.error('Error checking Google Calendar connection:', error);
      setUserInfo(null);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle sign-in via backend
  const handleSignIn = async () => {
    setIsLoading(true);
    
    try {
      // Directly redirect to Google auth using the main OAuth flow endpoint
      const isDevelopment = process.env.NODE_ENV === 'development';
      const endpoint = isDevelopment 
        ? 'http://localhost:3001/api/auth/google'
        : '/api/auth/google';
      
      window.location.href = endpoint;
      return true;
    } catch (error) {
      console.error('Error starting Google sign in:', error);
      setIsLoading(false);
      return false;
    }
  };

  // Handle sign-out via backend
  const handleSignOut = async () => {
    setIsLoading(true);
    
    try {
      await api.post('/api/auth/google/disconnect');
      setUserInfo(null);
      return true;
    } catch (error) {
      console.error('Error disconnecting from Google:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Sync birthday event with Google Calendar via backend
  const syncEvent = async (birthdayEvent: BirthdayEvent): Promise<string> => {
    setIsLoading(true);
    
    try {
      // Check connection first if we don't have user info
      if (!userInfo) {
        const connected = await checkGoogleConnection();
        if (!connected) {
          // User not connected, redirect to sign in
          await handleSignIn();
          return '';
        }
      }
      
      const response = await api.post('/api/calendar/sync', {
        name: birthdayEvent.name,
        date: birthdayEvent.date,
        reminderDays: birthdayEvent.reminderDays,
        appEventId: birthdayEvent.appEventId,
        emoji: birthdayEvent.emoji
      });
      
      setIsLoading(false);
      return response.data.eventId;
    } catch (error: any) {
      console.error('Error syncing with Google Calendar:', error);
      setIsLoading(false);
      
      // If we get a 401 or 403 error, the user needs to sign in again
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // Show a message that we're redirecting for permissions
        console.log('Need calendar permissions, redirecting to Google sign-in...');
        alert('We need your permission to access Google Calendar. Redirecting to Google sign-in...');
        
        // Redirect to sign in
        await handleSignIn();
        return '';
      }
      
      throw error;
    }
  };

  // Remove event from Google Calendar via backend
  const removeEvent = async (appEventId: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      // Check connection first if we don't have user info
      if (!userInfo) {
        const connected = await checkGoogleConnection();
        if (!connected) {
          // User not connected, redirect to sign in
          await handleSignIn();
          return false;
        }
      }
      
      await api.delete(`/api/calendar/sync/${appEventId}`);
      setIsLoading(false);
      return true;
    } catch (error: any) {
      console.error('Error removing event from Google Calendar:', error);
      setIsLoading(false);
      
      // If we get a 401 or 403 error, the user needs to sign in again
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // Show a message that we're redirecting for permissions
        console.log('Need calendar permissions, redirecting to Google sign-in...');
        alert('We need your permission to access Google Calendar. Redirecting to Google sign-in...');
        
        // Redirect to sign in
        await handleSignIn();
        return false;
      }
      
      throw error;
    }
  };

  return {
    isLoading,
    userInfo,
    checkGoogleConnection,
    handleSignIn,
    handleSignOut,
    syncEvent,
    removeEvent
  };
};

export default useGoogleCalendar; 